// Validación del `?encounter=` que viaja en la URL de un formulario (ver
// enlaceEncounter.ts) — hallazgo del revisor funcional, 2026-09-14/16:
// FormularioDiscretoPage/MotorFormularioPage/Formulario001Page tomaban ese
// query param y armaban `{ reference: encounterParam }` sin comprobar que el
// Encounter perteneciera al paciente de la ruta. No cruza tenant ni paciente
// para LEER (la cuenta ya autenticada puede ver a ambos dentro del tenant),
// pero con manipulación deliberada de la URL se puede cruzar el vínculo
// Encounter↔registro (una nota de un paciente A queda enlazada a la
// consulta de un paciente B). Este hook centraliza el chequeo: solo deja
// pasar el Encounter si su `subject` coincide con el `subject` de la ruta;
// si no, se comporta fail-closed (como si no hubiera Encounter en la URL) e
// informa `invalido` para que la pantalla pueda avisarlo sin bloquear.
import type { Encounter, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';

export type EstadoEncounterValidado =
  | { cargando: true }
  | {
      cargando: false;
      /** Referencia lista para pasarle a un formulario — `undefined` si no había param o no era del paciente. */
      encounter: Reference<Encounter> | undefined;
      /** El recurso completo, para quien lo necesite (ej. Formulario001Page precarga sus campos de admisión). */
      recurso: Encounter | undefined;
      /** `true` solo cuando el param traía algo pero NO pertenecía al paciente (o no se pudo leer) — para avisar, no bloquea. */
      invalido: boolean;
    };

export function useEncounterValidado(subject: Reference<Patient>, encounterParam: string | null): EstadoEncounterValidado {
  const medplum = useMedplum();
  const [resultado, setResultado] = useState<{ encounter: Encounter | undefined; invalido: boolean } | undefined>(undefined);

  useEffect(() => {
    if (!encounterParam) {
      setResultado({ encounter: undefined, invalido: false });
      return;
    }
    // Vuelve a "cargando" mientras se valida el nuevo param — evita mostrar
    // por un instante el Encounter validado del render anterior si el
    // paciente/param cambiaron (ej. navegación directa entre pacientes).
    setResultado(undefined);
    let cancelado = false;
    medplum
      .readReference<Encounter>({ reference: encounterParam })
      .then((e) => {
        if (cancelado) return;
        const pertenece = e.subject?.reference === subject.reference;
        setResultado({ encounter: pertenece ? e : undefined, invalido: !pertenece });
      })
      .catch(() => {
        if (!cancelado) setResultado({ encounter: undefined, invalido: true });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, subject.reference, encounterParam]);

  if (resultado === undefined) {
    return { cargando: true };
  }
  const ref = resultado.encounter?.id ? { reference: `Encounter/${resultado.encounter.id}` } : undefined;
  return { cargando: false, encounter: ref, recurso: resultado.encounter, invalido: resultado.invalido };
}
